import { TIMELINE_STEPS } from '../utils/cardFlowConfig'
import { formatDateTime } from '../utils/formatters'
import { STEP_ICONS, DEFAULT_STEP_ICON } from '../utils/timelineIcons'

/**
 * Round marker of one stage, in three clearly different states:
 *  - completed: solid white disc with a check;
 *  - current:   white ring with the stage icon and a faint halo;
 *  - pending:   hairline ring with a muted stage icon.
 */
function Marker({ state, iconName, label }) {
  const base = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors'
  if (state === 'completed') {
    return (
      <span className={`${base} bg-white text-m1-ground`} title={label}>
        <i className="ri-check-line text-[17px] leading-none" aria-hidden="true" />
        <span className="sr-only">Completed</span>
      </span>
    )
  }
  if (state === 'current') {
    return (
      <span
        className={`${base} border border-white bg-m1-ground text-white shadow-[0_0_0_4px_rgba(255,255,255,0.08)]`}
        title={label}
      >
        <i className={`${iconName} text-[16px] leading-none`} aria-hidden="true" />
        <span className="sr-only">Current step</span>
      </span>
    )
  }
  return (
    <span className={`${base} border border-white/20 text-m1-muted`} title={label}>
      <i className={`${iconName} text-[16px] leading-none`} aria-hidden="true" />
      <span className="sr-only">Pending</span>
    </span>
  )
}

/**
 * Horizontal timeline (modal / flows), vertical on phones. For the card page see
 * GradingHistoryTimeline.
 */
export default function FlowTimeline({ currentStep = 0, steps = TIMELINE_STEPS, dates = {} }) {
  const stateOf = (index) => {
    const stepNum = index + 1
    if (currentStep > stepNum) return 'completed'
    if (currentStep === stepNum) return 'current'
    return 'pending'
  }

  return (
    <div className="flow-timeline">
      <ol className="hidden items-start sm:flex">
        {steps.map((step, index) => {
          const state = stateOf(index)
          const date = dates[step.key]
          const reached = currentStep >= index + 1
          const leaving = state === 'completed'

          return (
            <li key={step.key} className="flex min-w-0 flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                <span
                  className={`h-px flex-1 ${index === 0 ? 'bg-transparent' : reached ? 'bg-white/60' : 'bg-white/15'}`}
                  aria-hidden="true"
                />
                <Marker
                  state={state}
                  iconName={STEP_ICONS[step.key] || DEFAULT_STEP_ICON}
                  label={step.label}
                />
                <span
                  className={`h-px flex-1 ${index === steps.length - 1 ? 'bg-transparent' : leaving ? 'bg-white/60' : 'bg-white/15'}`}
                  aria-hidden="true"
                />
              </div>
              <span
                className={`mt-3 w-full truncate px-1 text-center text-[13px] ${
                  state === 'current'
                    ? 'font-medium text-m1-ink'
                    : state === 'completed'
                      ? 'text-m1-ink-soft'
                      : 'text-m1-muted'
                }`}
              >
                {step.label}
              </span>
              {date && (
                <span className="mt-1 px-1 text-center font-m1-mono text-[10px] leading-snug text-m1-muted">
                  {formatDateTime(date)}
                </span>
              )}
            </li>
          )
        })}
      </ol>

      <ol className="sm:hidden">
        {steps.map((step, index) => {
          const state = stateOf(index)
          const date = dates[step.key]
          const isLast = index === steps.length - 1

          return (
            <li
              key={step.key}
              className={`relative flex items-start gap-4 ${isLast ? '' : 'pb-5'}`}
            >
              {!isLast && (
                <span
                  className={`absolute bottom-0 left-4 top-8 w-px ${
                    state === 'completed' ? 'bg-white/60' : 'bg-white/15'
                  }`}
                  aria-hidden="true"
                />
              )}
              <Marker
                state={state}
                iconName={STEP_ICONS[step.key] || DEFAULT_STEP_ICON}
                label={step.label}
              />
              <div className="min-w-0 flex-1 pt-1">
                <p
                  className={`text-[14px] ${
                    state === 'current'
                      ? 'font-medium text-m1-ink'
                      : state === 'completed'
                        ? 'text-m1-ink-soft'
                        : 'text-m1-muted'
                  }`}
                >
                  {step.label}
                </p>
                {date && (
                  <p className="mt-0.5 font-m1-mono text-[11px] leading-snug text-m1-muted">
                    {formatDateTime(date)}
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
